import {
  faEdit,
  faEllipsisVertical,
  faEnvelope,
  faForward,
  faGlobe,
  faMailForward,
  faRefresh,
  faReply,
  faReplyAll,
  faRightFromBracket,
  faTrash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as z from "zod";
import imgs from "../../../Utils/assets/img/faces/5.jpg";
import { formatDate, formatTime } from "../../../Utils/helpers/commonhelper";
import { Button, Dropdown, Nav } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import DeleteModal from "../../DeleteModal";
import ReactQuill from "react-quill";
import styles from "./outlook.module.css";
import "react-quill/dist/quill.snow.css";
import { toast } from "sonner";

export const composeSchema = z.object({
  to: z
    .string()
    .trim()
    .min(1, "Recipient is required")
    .refine(
      (val) =>
        val
          .split(",")
          .map((v) => v.trim())
          .every((v) => {
            const match = v.match(
              /<?([\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})>?/,
            );
            return !!match;
          }),
      "Invalid recipient email(s)",
    ),
  cc: z
    .string()
    .optional()
    .refine(
      (val) =>
        !val ||
        val
          .split(",")
          .map((v) => v.trim())
          .every((v) => {
            const match = v.match(
              /<?([\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})>?/,
            );
            return !!match;
          }),
      "Invalid CC email(s)",
    ),
  bcc: z
    .string()
    .optional()
    .refine(
      (val) =>
        !val ||
        val
          .split(",")
          .map((v) => v.trim())
          .every((v) => {
            const match = v.match(
              /<?([\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})>?/,
            );
            return !!match;
          }),
      "Invalid BCC email(s)",
    ),
  subject: z.string().min(1, "Subject is required"),
  body: z.string().min(1, "Message body is required"),
  attachments: z.array(z.instanceof(File)).optional(),
});

const MS_GRAPH_ENDPOINT = "https://graph.microsoft.com/v1.0";

function base64UrlEncode(str) {
  const utf8Str = unescape(encodeURIComponent(str));
  return btoa(utf8Str)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function toHeaderDate(date = new Date()) {
  return date.toUTCString();
}

function getHeader(headers = [], name) {
  return (
    headers.find((h) => h.name?.toLowerCase() === name.toLowerCase())?.value ||
    ""
  );
}

function decodeBase64Url(str = "") {
  try {
    return decodeURIComponent(
      escape(window.atob(str.replace(/-/g, "+").replace(/_/g, "/"))),
    );
  } catch {
    return "";
  }
}

function collectParts(part, acc = []) {
  if (!part) return acc;
  acc.push(part);
  if (Array.isArray(part.parts)) {
    for (const p of part.parts) collectParts(p, acc);
  }
  return acc;
}

async function fileToBase64(file) {
  const buf = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function EmailCrm() {
  const navigate = useNavigate();

  const token = sessionStorage.getItem("email_access_token");
  let service = sessionStorage.getItem("email_service");

  // Self-heal: this component only ever renders when Gmail is the active
  // connected service (EmailContainer already decided that). If something
  // else in the background (e.g. an Outlook status check) clears just the
  // "email_service" key while the token is still valid, don't drop the
  // user's session for it — restore it instead.
  if (token && service !== "gmail") {
    sessionStorage.setItem("email_service", "gmail");
    service = "gmail";
  }

  const isConnected = !!token;
  const [tab, setTab] = useState("inbox");
  const [search, setSearch] = useState("");
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteModal1, setShowDeleteModal1] = useState(false);

  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState("new");
  const [sending, setSending] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const [collapsedIds, setCollapsedIds] = React.useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [designationToDelete, setDesignationToDelete] = useState(null);
  const [composeData, setComposeData] = useState({
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    body: "",
    attachments: [],
    threadId: undefined,
    inReplyTo: undefined,
    references: undefined,
  });

  const handleEmailAuthError = () => {
    sessionStorage.removeItem("email_access_token");
    sessionStorage.removeItem("email_service");
    sessionStorage.removeItem("email_token_expiry");
    sessionStorage.removeItem("user_email");

    setThreads([]);
    setSelectedThread(null);

    // optional UI error
    setError("Email session expired. Please connect again.");
  };
  const [previewImage, setPreviewImage] = useState(null);

  const openImagePreview = (src) => {
    setPreviewImage(src);
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  // Ensure pugCrm label exists (Gmail)
  const ensureGmailLabel = useCallback(async () => {
    const res = await fetch(
      "https://gmail.googleapis.com/gmail/v1/users/me/labels",
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (!res.ok) {
      if ([401, 403, 404].includes(res.status)) {
        handleEmailAuthError();
        return;
      }
    }
    const data = await res.json();

    let pug = data.labels?.find((l) => l.name.toLowerCase() === "pugcrm");
    if (!pug) {
      const createRes = await fetch(
        "https://gmail.googleapis.com/gmail/v1/users/me/labels",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: "pugCrm",
            labelListVisibility: "labelShow",
            messageListVisibility: "show",
          }),
        },
      );
      pug = await createRes.json();
    }
    return pug;
  }, [token]);

  const fetchThreadList = useCallback(async () => {
    if (!isConnected || service !== "gmail") {
      return;
    }

    setLoading(true);
    setSelectedThread(null);
    setError("");
    try {
      const pug = await ensureGmailLabel();
      const labelIds = [pug.id];

      let q = "";
      if (tab === "inbox") q = "";
      else if (tab === "sent") q = "in:sent";

      if (search) q = q ? `${q} ${search}` : search;

      const listUrl = `https://gmail.googleapis.com/gmail/v1/users/me/threads?labelIds=${labelIds.join(
        "&labelIds=",
      )}${q ? `&q=${encodeURIComponent(q)}` : ""}&maxResults=50`;

      const listRes = await fetch(listUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!listRes.ok) {
        if ([401, 403, 404].includes(listRes.status)) {
          handleEmailAuthError();
          return;
        }
        throw new Error("Failed to fetch threads");
      }
      const listData = await listRes.json();
      const threadIds = Array.isArray(listData.threads)
        ? listData.threads.map((t) => t.id)
        : [];

      if (!threadIds.length) {
        setThreads([]);
        return;
      }

      const batchSize = 5;
      let fullThreads = [];
      for (let i = 0; i < threadIds.length; i += batchSize) {
        const batchIds = threadIds.slice(i, i + batchSize);
        const batch = await Promise.all(
          batchIds.map((id) =>
            fetch(
              `https://gmail.googleapis.com/gmail/v1/users/me/threads/${id}?format=full`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            ).then((r) => r.json()),
          ),
        );
        fullThreads = fullThreads.concat(batch);
        await delay(200);
      }

      const normalized = fullThreads
        .map((th) => {
          const messages = th.messages || [];

          const sorted = [...messages].sort((a, b) => {
            const da =
              Number(a.internalDate) ||
              new Date(getHeader(a.payload?.headers, "Date")).getTime() ||
              0;
            const db =
              Number(b.internalDate) ||
              new Date(getHeader(b.payload?.headers, "Date")).getTime() ||
              0;
            return db - da;
          });
          const latest = sorted[0];
          const subject =
            getHeader(latest?.payload?.headers, "Subject") || "(No Subject)";
          const from = getHeader(latest?.payload?.headers, "From");
          const snippet = latest?.snippet || "";
          const lbls = new Set(latest?.labelIds || []);
          return {
            id: th.id,
            messagesCount: messages.length,
            latest: {
              subject,
              from,
              snippet,
              date: getHeader(latest?.payload?.headers, "Date"),
            },
            hasInbox: lbls.has("INBOX"),
            hasSent: lbls.has("SENT"),
          };
        })
        .sort(
          (a, b) =>
            new Date(b.latest.date).getTime() -
            new Date(a.latest.date).getTime(),
        );

      setThreads(normalized);
    } catch (e) {
      setError(e?.message || "Failed to fetch threads");
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, [ensureGmailLabel, isConnected, search, service, tab, token]);

  useEffect(() => {
    if (isConnected) fetchThreadList();
  }, [isConnected, tab, fetchThreadList]);

  const openThread = useCallback(
    async (threadId) => {
      if (service !== "gmail") return;
      setLoading(true);
      setError("");
      try {
        const thRes = await fetch(
          `https://gmail.googleapis.com/gmail/v1/users/me/threads/${threadId}?format=full`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const th = await thRes.json();
        const messages = th.messages || [];

        const processed = await Promise.all(
          messages.map(async (m) => {
            const headers = m.payload?.headers || [];
            const subject = getHeader(headers, "Subject");
            const from = getHeader(headers, "From");
            const to = getHeader(headers, "To");
            const cc = getHeader(headers, "Cc");
            const bcc = getHeader(headers, "Bcc");
            const date = getHeader(headers, "Date");
            const messageId = getHeader(headers, "Message-Id");

            const parts = collectParts(m.payload);
            let html = "";
            let text = "";
            for (const p of parts) {
              if (p.mimeType === "text/html" && p.body?.data)
                html = decodeBase64Url(p.body.data);
              else if (p.mimeType === "text/plain" && p.body?.data)
                text = decodeBase64Url(p.body.data);
            }
            let body = html || text || m.snippet || "(No Content)";

            const cidMap = {};
            const attachments = [];
            for (const p of parts) {
              const filename = p.filename;
              const attId = p.body?.attachmentId;
              const mime = p.mimeType;
              // if (attId) {
              //   const aRes = await fetch(
              //     `https://gmail.googleapis.com/gmail/v1/users/me/messages/${m.id}/attachments/${attId}`,
              //     { headers: { Authorization: `Bearer ${token}` } }
              //   );
              //   const aData = await aRes.json();

              //   const data = aData?.data || "";
              //   const normalized = data.replace(/-/g, "+").replace(/_/g, "/");
              //   const dataUrl = `data:${mime};base64,${normalized}`;

              //   const cId = p.headers?.find(
              //     (h) => h.name?.toLowerCase() === "content-id"
              //   )?.value;
              //   if (cId) {
              //     const clean = cId.replace(/[<>]/g, "");
              //     cidMap[clean] = dataUrl;
              //   } else {
              //     attachments.push({ filename, mime, dataUrl });
              //   }
              // }

              if (attId && filename) {
                const aRes = await fetch(
                  `https://gmail.googleapis.com/gmail/v1/users/me/messages/${m.id}/attachments/${attId}`,
                  { headers: { Authorization: `Bearer ${token}` } },
                );

                const aData = await aRes.json();
                const normalized = aData.data
                  .replace(/-/g, "+")
                  .replace(/_/g, "/");
                const dataUrl = `data:${mime};base64,${normalized}`;

                attachments.push({
                  filename,
                  mime,
                  dataUrl,
                });
              }
            }

            body = body.replace(/src=["']cid:([^"']+)["']/gi, (m0, cid) => {
              const url = cidMap[cid];
              return url ? `src="${url}"` : m0;
            });

            return {
              id: m.id,
              threadId: m.threadId,
              subject,
              from,
              to,
              cc,
              bcc,
              date,
              messageId,
              body,
              attachments,
            };
          }),
        );

        processed.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );

        setSelectedThread({ id: threadId, messages: processed });
      } catch (e) {
        setError(e?.message || "Failed to open thread");
      } finally {
        setLoading(false);
      }
    },
    [service, token],
  );

  function dataURLtoFile(dataUrl, filename) {
    const [header, base64] = dataUrl.split(",");
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const byteString = atob(base64);
    const n = byteString.length;
    const u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      u8arr[i] = byteString.charCodeAt(i);
    }
    return new File([u8arr], filename, { type: mime });
  }

  const openCompose = useCallback(
    (mode = "new") => {
      setComposeMode(mode);
      setComposeOpen(true);

      if (
        (mode === "reply" || mode === "replyAll" || mode === "forward") &&
        selectedThread
      ) {
        const last =
          selectedThread.messages[selectedThread.messages.length - 1];

        const myEmail = sessionStorage.getItem("user_email") || "";
        const fromEmail = last?.from?.match(/<(.+?)>/)?.[1] || last?.from || "";
        const normalize = (email) => email?.trim().toLowerCase();

        const myEmailNorm = normalize(myEmail);
        const fromEmailNorm = normalize(fromEmail);
        const toList = last?.to
          ? last.to
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean)
          : [];
        const ccList = last?.cc
          ? last.cc
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean)
          : [];
        const bccList = last?.bcc
          ? last.bcc
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean)
          : [];

        if (mode === "reply" || mode === "replyAll") {
          let replyTo = "";
          let replyCc = "";
          let replyBcc = "";
          if (mode === "reply") {
            //  Simple reply
            replyTo = fromEmailNorm
              ? toList.find((e) => normalize(e) !== myEmailNorm) || ""
              : fromEmail;
            replyCc = "";
          } else {
            //  Reply All logic
            if (fromEmailNorm) {
              replyTo = toList
                .filter((e) => normalize(e) !== myEmailNorm)
                .join(", ");
            } else {
              replyTo = fromEmail;
            }

            replyCc = ccList.join(", ");
            replyBcc = bccList.join(", ");
            // .filter(
            //   (e) =>
            //     normalize(e) !== myEmailNorm &&
            //     !replyTo.split(",").some((t) => normalize(t) === normalize(e))
            // )
            //
          }

          //  Subject line fix
          const subj = last.subject?.toLowerCase().startsWith("re:")
            ? last.subject
            : `Re: ${last.subject}`;

          //  Quoted body
          const quoted = `
          <br><br>
          <p>On ${last.date}, ${last.from} wrote:</p>
          <blockquote style="border-left:2px solid #ccc;padding-left:10px;margin-left:0;">
            ${last.body}
          </blockquote>
        `;

          setComposeData({
            to: replyTo,
            cc: replyCc,
            bcc: replyBcc,
            subject: subj,
            body: quoted,
            attachments: [],
            threadId: last.threadId,
            inReplyTo: last.messageId,
            references: last.messageId,
          });
        }

        //  Forward
        else if (mode === "forward") {
          const subj = last.subject?.toLowerCase().startsWith("fwd:")
            ? last.subject
            : `Fwd: ${last.subject}`;

          const fwdHTML = `
          <br><br>
          <p>---------- Forwarded message ----------</p>
          <p><b>From:</b> ${last.from}<br>
          <b>Date:</b> ${last.date}<br>
          <b>Subject:</b> ${last.subject}<br>
          <b>To:</b> ${last.to}<br>
          ${last.cc ? `<b>Cc:</b> ${last.cc}<br>` : ""}</p>
          <blockquote style="border-left:2px solid #ccc;padding-left:10px;margin-left:0;">
            ${last.body}
          </blockquote>
        `;

          const existingAttachments = Array.isArray(last.attachments)
            ? last.attachments
                .filter((a) => a.dataUrl && a.filename)
                .map((a) => dataURLtoFile(a.dataUrl, a.filename))
            : [];

          setComposeData({
            to: "",
            cc: "",
            bcc: "",
            subject: subj,
            body: fwdHTML,
            attachments: existingAttachments,
            threadId: undefined,
            inReplyTo: undefined,
            references: undefined,
          });
        }
      } else {
        //  New mail
        setComposeData({
          to: "",
          cc: "",
          bcc: "",
          subject: "",
          body: "",
          attachments: [],
          threadId: undefined,
          inReplyTo: undefined,
          references: undefined,
        });
      }
    },
    [selectedThread],
  );

  const onSelectFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setComposeData((d) => ({
      ...d,
      attachments: [...(d.attachments || []), ...files],
    }));
  };

  const formatZodError = (zodError) => {
    if (!zodError?.issues) return "Invalid input";

    return zodError.issues.map((issue) => issue.message).join(", ");
  };

  const sendEmail = useCallback(async () => {
    setError("");
    if (!service || !token) return;
    try {
      const parsed = composeSchema.parse({
        to: composeData.to,
        cc: composeData.cc || undefined,
        bcc: composeData.bcc || undefined,
        subject: composeData.subject,
        body: composeData.body,
        attachments: composeData.attachments,
      });

      if (service === "gmail") {
        const pug = await ensureGmailLabel();

        const boundary =
          "pugcrm_boundary_" + Math.random().toString(36).slice(2);
        const headers = [
          `Date: ${toHeaderDate()}`,
          `To: ${parsed.to}`,
          parsed.cc ? `Cc: ${parsed.cc}` : null,
          parsed.bcc ? `Bcc: ${parsed.bcc}` : null,
          `Subject: ${parsed.subject}`,
          `From: me`,
          `MIME-Version: 1.0`,
          `Content-Type: multipart/mixed; boundary="${boundary}"`,
          composeData.inReplyTo
            ? `In-Reply-To: ${composeData.inReplyTo}`
            : null,
          composeData.references
            ? `References: ${composeData.references}`
            : null,
        ]
          .filter(Boolean)
          .join("\r\n");

        const htmlPart = [
          `--${boundary}`,
          `Content-Type: text/html; charset="UTF-8"`,
          `Content-Transfer-Encoding: 7bit`,
          ``,
          parsed.body,
          ``,
        ].join("\r\n");

        let attachmentsPart = "";
        if (parsed.attachments && parsed.attachments.length) {
          for (const f of parsed.attachments) {
            const b64 = await fileToBase64(f);
            attachmentsPart += [
              `--${boundary}`,
              `Content-Type: ${f.type || "application/octet-stream"}; name="${
                f.name
              }"`,
              `Content-Disposition: attachment; filename="${f.name}"`,
              `Content-Transfer-Encoding: base64`,
              ``,
              b64,
              ``,
            ].join("\r\n");
          }
        }

        const closing = `--${boundary}--`;

        const mime = [headers, "", htmlPart, attachmentsPart, closing].join(
          "\r\n",
        );
        const raw = base64UrlEncode(mime);

        const body = composeData.threadId
          ? { raw, threadId: composeData.threadId }
          : { raw };

        const sendRes = await fetch(
          "https://gmail.googleapis.com/gmail/v1/users/me/messages/send",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          },
        );
        if (!sendRes.ok) {
          const err = await sendRes.json();
          throw new Error(
            `Gmail send error: ${sendRes.status} ${JSON.stringify(err)}`,
          );
        }
        const sentMessage = await sendRes.json();

        await fetch(
          `https://gmail.googleapis.com/gmail/v1/users/me/messages/${sentMessage.id}/modify`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              addLabelIds: [pug.id],
            }),
          },
        );

        setComposeOpen(false);
        setComposeData({
          to: "",
          cc: "",
          bcc: "",
          subject: "",
          body: "",
          attachments: [],
          threadId: undefined,
          inReplyTo: undefined,
          references: undefined,
        });

        if (composeData.threadId) {
          await openThread(composeData.threadId);
        } else {
          await fetchThreadList();
        }
      } else if (service === "outlook") {
        const attachments = [];
        if (parsed.attachments?.length) {
          for (const f of parsed.attachments) {
            const b64 = await fileToBase64(f);
            attachments.push({
              "@odata.type": "#microsoft.graph.fileAttachment",
              name: f.name,
              contentType: f.type || "application/octet-stream",
              contentBytes: b64,
            });
          }
        }
        const message = {
          subject: parsed.subject,
          body: {
            contentType: "HTML",
            content: parsed.body,
          },
          toRecipients: [{ emailAddress: { address: parsed.to } }],
          ccRecipients: parsed.cc
            ? parsed.cc
                .split(",")
                .map((a) => ({ emailAddress: { address: a.trim() } }))
            : [],
          bccRecipients: parsed.bcc
            ? parsed.bcc
                .split(",")
                .map((a) => ({ emailAddress: { address: a.trim() } }))
            : [],
          attachments,
          categories: ["PugCRM"],
        };

        const sendRes = await fetch(`${MS_GRAPH_ENDPOINT}/me/sendMail`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ message, saveToSentItems: true }),
        });
        if (!sendRes.ok) {
          const err = await sendRes.json();
          throw new Error(
            `Outlook send error: ${sendRes.status} ${JSON.stringify(err)}`,
          );
        }
        setComposeOpen(false);
        await fetchThreadList();
      }
    } catch (e) {
      if (e?.issues) {
        // Zod validation error
        const readableMessage = formatZodError(e);
        setError(readableMessage);
        toast.error(readableMessage);
      } else {
        setError(e?.message || "Failed to send email");
        toast.error("Failed to send email");
      }
    } finally {
      setSending(false);
    }
  }, [
    composeData,
    ensureGmailLabel,
    fetchThreadList,
    openThread,
    service,
    token,
  ]);

  const onSend = async () => {
    if (sending) return;
    setSending(true);
    await sendEmail();
  };

  const onDelete = (id) => {
    setDesignationToDelete(id);
    setShowDeleteModal1(true);
  };

  const deleteThread = useCallback(
    async (threadId) => {
      try {
        if (service !== "gmail" || !token) return;
        const pug = await ensureGmailLabel();
        await fetch(
          `https://gmail.googleapis.com/gmail/v1/users/me/threads/${threadId}/modify`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ removeLabelIds: [pug.id] }),
          },
        );
        await fetch(
          `https://gmail.googleapis.com/gmail/v1/users/me/threads/${threadId}/trash`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setThreads((prev) => prev.filter((t) => t.id !== threadId));
        if (selectedThread?.id === threadId) {
          setSelectedThread(null);
        }
      } catch (e) {
        setError(e?.message || "Failed to delete thread");
      } finally {
        setShowDeleteModal1(false);
        setDesignationToDelete(null);
      }
    },
    [ensureGmailLabel, selectedThread?.id, service, token],
  );

  const deleteAllInView = useCallback(async () => {
    if (service !== "gmail" || !token || !threads.length) return;
    try {
      setDeletingAll(true);
      const pug = await ensureGmailLabel();
      const ids = threads
        .filter((t) =>
          tab === "inbox" ? t.hasInbox : tab === "sent" ? t.hasSent : true,
        )
        .map((t) => t.id);

      for (const id of ids) {
        try {
          await fetch(
            `https://gmail.googleapis.com/gmail/v1/users/me/threads/${id}/modify`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ removeLabelIds: [pug.id] }),
            },
          );
          await fetch(
            `https://gmail.googleapis.com/gmail/v1/users/me/threads/${id}/trash`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          await delay(150);
        } catch (err) {
          console.error("[v0] delete thread error:", err);
        }
      }
      await fetchThreadList();
    } catch (e) {
      setError(e?.message || "Failed to delete all");
    } finally {
      setDeletingAll(false);
    }
  }, [ensureGmailLabel, fetchThreadList, service, tab, threads, token]);

  const deleteSelectedThreads = useCallback(async () => {
    if (service !== "gmail" || !token || !selectedIds.length) return;
    try {
      setDeletingAll(true);
      const pug = await ensureGmailLabel();

      for (const id of selectedIds) {
        await fetch(
          `https://gmail.googleapis.com/gmail/v1/users/me/threads/${id}/modify`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ removeLabelIds: [pug.id] }),
          },
        );
        await fetch(
          `https://gmail.googleapis.com/gmail/v1/users/me/threads/${id}/trash`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        await new Promise((res) => setTimeout(res, 150));
      }

      setThreads((prev) => prev.filter((t) => !selectedIds.includes(t.id)));
      setSelectedIds([]);
    } catch (e) {
      setError(e?.message || "Failed to delete selected threads");
    } finally {
      setDeletingAll(false);
      setShowDeleteModal(false);
    }
  }, [ensureGmailLabel, service, token, selectedIds]);

  const counts = useMemo(() => {
    return {
      total: threads.length,
      inbox: threads.filter((t) => t.hasInbox).length,
      sent: threads.filter((t) => t.hasSent).length,
    };
  }, [threads]);

  const handleComposedClose = () => {
    setComposeOpen(false);
    setError("");
  };

  //   const handleDisconnect = () => {
  //   sessionStorage.clear();
  //   toast.success("Disconnected");
  //   navigate("/email-settings")
  // };
  const userEmail = sessionStorage.getItem("user_email");
  const userName = sessionStorage.getItem("user_name");
  const services = sessionStorage.getItem("email_service");
  const handleDisconnect = async () => {
    sessionStorage.clear();
    toast.success("Disconnected");
    navigate("/email-settings");
  };
  return (
    <div className="p-3" style={{ backgroundColor: "#f5f6fa" }}>
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => deleteSelectedThreads()}
        message={"Are you sure you want to delete all selected email ?"}
      />
      <DeleteModal
        show={showDeleteModal1}
        onClose={() => setShowDeleteModal1(false)}
        onConfirm={() => deleteThread(designationToDelete)}
        message={"Are you sure you want to delete this email ?"}
      />

      <div className="row g-2" style={{ height: "100%" }}>
        <div className={styles.container}>
          {/* Left Sidebar */}
          <div
            className="d-flex flex-column bg-white rounded border"
            style={{ width: "17%" }}
          >
            <div className=" p-3" style={{ height: "100%" }}>
              <button
                className="btn btn-success w-100 fw-semibold"
                onClick={() => openCompose("new")}
                disabled={!isConnected}
              >
                + Compose Mail
              </button>
              <hr />
              <div className="p-3">
                <div className="text-muted fw-semibold small mb-2">MAILS</div>
                <div className="nav flex-column gap-2">
                  <button
                    className={`nav-link d-flex align-items-center justify-content-between ${
                      tab === "all" ? "active" : ""
                    }`}
                    onClick={() => setTab("all")}
                  >
                    <span>All Mails</span>
                    <span className="badge bg-success-subtle text-success fw-semibold">
                      {counts.total}
                    </span>
                  </button>
                  <button
                    className={`nav-link d-flex align-items-center justify-content-between ${
                      tab === "inbox" ? "active" : ""
                    }`}
                    onClick={() => setTab("inbox")}
                  >
                    <span>Inbox</span>
                    <span className="badge bg-primary-subtle text-primary fw-semibold">
                      {counts.inbox}
                    </span>
                  </button>
                  <button
                    className={`nav-link d-flex align-items-center justify-content-between ${
                      tab === "sent" ? "active" : ""
                    }`}
                    onClick={() => setTab("sent")}
                  >
                    <span className="d-flex align-items-center">
                      <span
                        className="me-2"
                        style={{ transform: "rotate(-45deg)" }}
                      ></span>
                      Sent
                    </span>
                    <span className="badge bg-secondary-subtle text-secondary fw-semibold">
                      {counts.sent}
                    </span>
                  </button>
                </div>
              </div>
            </div>
            <div className="p-3 border-top flex-shrink-0">
              <div className="mb-3 d-flex flex-column gap-2 text-muted small">
                {/* User Name */}
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="text-secondary"
                    style={{ width: "16px", textAlign: "center" }}
                  />
                  <span className="fw-semibold text-dark text-truncate">
                    {userName || "saurabh"}
                  </span>
                </div>

                {/* User Email */}
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="text-secondary"
                    style={{ width: "16px", textAlign: "center" }}
                  />
                  <span className="text-truncate" title={userEmail}>
                    {userEmail || "email@example.com"}
                  </span>
                </div>

                {/* Service Name */}
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="text-secondary"
                    style={{ width: "16px", textAlign: "center" }}
                  />
                  <span className="badge bg-light text-dark border text-truncate">
                    {services || "Gmail Service"}
                  </span>
                </div>
              </div>

              {/* Log Out Button */}
              <button
                className="btn btn-danger w-100 fw-semibold d-flex align-items-center justify-content-center gap-2"
                onClick={handleDisconnect}
              >
                <FontAwesomeIcon icon={faRightFromBracket} />
                <span>Log Out</span>
              </button>
            </div>
            {/* <button
          
             style={{marginTop: "100%" }}
              className="btn btn-danger  fw-semibold cursor-pointer m-3"
              onClick={handleDisconnect}
              // disabled={!isConnected}
            >
              <FontAwesomeIcon icon={faRightFromBracket} />
               <span className="ms-2">Log Out</span>
            </button> */}
          </div>

          {/* Middle section */}
          <div
            className="d-flex flex-column bg-white rounded border"
            style={{ width: "36%" }}
          >
            {/* Header Section */}
            <div className="p-3 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={
                    threads.length > 0 && selectedIds.length === threads.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(threads.map((t) => t.id));
                    } else {
                      setSelectedIds([]);
                    }
                  }}
                />
                <span className="fw-semibold">
                  {tab === "sent"
                    ? "Sent"
                    : tab === "inbox"
                      ? "Inbox"
                      : "All Mails"}
                </span>
              </div>

              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => fetchThreadList()}
                  disabled={!isConnected}
                  title="Refresh"
                >
                  <FontAwesomeIcon icon={faRefresh} />
                </button>

                <div
                  style={{
                    display: `${!selectedIds.length ? "none" : "block"}`,
                  }}
                >
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={
                      !isConnected || deletingAll || !selectedIds.length
                    }
                    title="Delete Selected"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>

            <hr className="m-0 p-0" />

            {/* Search Section */}
            <div className="px-3 py-3">
              <form
                className="d-flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  fetchThreadList();
                }}
              >
                <input
                  className="form-control"
                  placeholder="Search Email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-outline-secondary" type="submit">
                  Search
                </button>
              </form>
            </div>

            <hr className="m-0 p-0" />

            {/* Email List */}
            <div
              className="flex-grow-1 overflow-auto"
              style={{ height: "60vh" }}
            >
              {!isConnected ? (
                <div className="text-center mt-5">
                  <p>No email account connected</p>
                  <Button
                    variant="primary"
                    onClick={() => navigate("/email-settings")}
                  >
                    Connect Email Account
                  </Button>
                </div>
              ) : loading ? (
                <div className="p-4 text-center">Loading…</div>
              ) : !threads.length ? (
                <p className="text-muted text-center my-4">No Emails Found</p>
              ) : (
                threads.map((t) => {
                  const isActive = selectedThread?.id === t.id;
                  return (
                    <div
                      key={t.id}
                      className={`px-3 py-2 border-bottom d-flex align-items-center justify-content-between ${
                        isActive ? "bg-light" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-center gap-3 w-100">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedIds.includes(t.id)}
                          onChange={(e) => {
                            setSelectedIds((prev) =>
                              e.target.checked
                                ? [...prev, t.id]
                                : prev.filter((id) => id !== t.id),
                            );
                          }}
                        />

                        <div
                          className="rounded-circle bg-secondary-subtle"
                          style={{ width: 36, height: 36 }}
                          onClick={() => openThread(t.id)}
                        >
                          <img
                            src={imgs}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                            }}
                            className="rounded-circel"
                          />
                        </div>

                        <div
                          className="flex-grow-1"
                          onClick={() => openThread(t.id)}
                        >
                          <div className="d-flex flex-column align-items-start justify-content-between">
                            <strong
                              className="text-truncate me-3 text-primary"
                              style={{
                                maxWidth: "70%",
                                fontSize: "12px",
                                fontWeight: 300,
                              }}
                            >
                              {(t.latest.from || "").split("<")[0].trim() ||
                                "Unknown"}
                            </strong>

                            <div
                              className="text-black text-truncate"
                              style={{ fontSize: "14px", fontWeight: 400 }}
                            >
                              {t.latest.subject && t.latest.subject.length > 30
                                ? `${t.latest.subject.slice(0, 30)}...`
                                : t.latest.subject || "(No Subject)"}
                            </div>

                            <div
                              className="text-muted"
                              style={{ fontSize: "12px", fontWeight: 400 }}
                              title={t?.latest?.snippet}
                            >
                              {t.latest.snippet && t.latest.snippet.length > 50
                                ? `${t.latest.snippet.slice(0, 50)}...`
                                : t.latest.snippet}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex flex-column align-items-end">
                          <small className="text-muted fs-10 mb-2">
                            {formatTime(
                              new Date(t.latest.date).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              }),
                            )}
                          </small>
                          <button
                            style={{ width: "27px", height: "27px" }}
                            className="btn btn-sm mt-1"
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(t.id);
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Preview */}
          <div className="d-flex flex-column" style={{ width: "47%" }}>
            <div className="bg-white rounded border px-3 pb-0 pt-3 flex-grow-1 overflow-auto">
              {!selectedThread ? (
                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-center text-muted">
                  <div
                    className="rounded-circle bg-secondary-subtle mb-3"
                    style={{ width: 56, height: 56 }}
                  />
                  <p className="mb-0">Select an email to preview</p>
                </div>
              ) : (
                <div>
                  {/* Header */}
                  <div className="d-flex justify-content-between pb-3 ">
                    <div className="d-flex align-items-center justify-content-between mb-0 ">
                      <div className="d-flex gap-2">
                        <div
                          className="rounded-circle bg-secondary-subtle d-flex align-items-center justify-content-center"
                          style={{ width: 36, height: 36 }}
                        >
                          <img
                            src={imgs}
                            alt="Sender"
                            width="100%"
                            height="100%"
                            className="rounded-circle"
                          />
                        </div>
                        <div>
                          <h6 className="mb-0">
                            {(() => {
                              const body =
                                selectedThread?.messages?.[0]?.body || "";
                              const fromHeader =
                                body.match(/From:\s*(.*)<(.*)>/i);
                              if (fromHeader && fromHeader[1]) {
                                return fromHeader[1].trim();
                              } else {
                                return (
                                  selectedThread?.messages?.[0]?.from ||
                                  "Unknown Sender"
                                );
                              }
                            })()}
                          </h6>

                          <span
                            className="text-muted"
                            style={{ fontWeight: 300 }}
                          >
                            {(() => {
                              const message = selectedThread?.messages?.[0];
                              const body = message?.body || "";
                              const hasForwardedFrom =
                                /From:\s*(.*)<(.*)>/i.test(body);

                              if (hasForwardedFrom) {
                                const fromEmail = message?.from?.includes("<")
                                  ? message.from
                                      .split("<")[1]
                                      .replace(">", "")
                                      .trim()
                                  : message?.from || "Unknown Sender";
                                return fromEmail;
                              } else {
                                return (
                                  selectedThread?.messages?.[
                                    selectedThread.messages.length - 1
                                  ]?.subject || "No Subject"
                                );
                              }
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="">
                      <Dropdown as="div" className="dropdownsWithoutToggle">
                        <Dropdown.Toggle as={Nav.Link} className="header-link">
                          <FontAwesomeIcon
                            style={{
                              width: "15px",
                              height: "15px",
                            }}
                            icon={faEllipsisVertical}
                          />
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          className="defaultDropdownLong plusDrowpdown dropdown-menu-end"
                          style={{ right: "3.4rem" }}
                        >
                          <Dropdown.Item
                            href="#"
                            className="d-flex align-items-center"
                            onClick={() => openCompose("reply")}
                          >
                            <FontAwesomeIcon icon={faReply} className="me-2" />{" "}
                            Reply
                          </Dropdown.Item>
                          <Dropdown.Item
                            href="#"
                            className="d-flex align-items-center"
                            onClick={() => openCompose("replyAll")}
                          >
                            <FontAwesomeIcon
                              icon={faReplyAll}
                              className="me-2"
                            />{" "}
                            Reply All
                          </Dropdown.Item>
                          <Dropdown.Item
                            href="#"
                            className="d-flex align-items-center"
                            onClick={() => openCompose("forward")}
                          >
                            <FontAwesomeIcon
                              icon={faMailForward}
                              className="me-2"
                            />{" "}
                            Forward
                          </Dropdown.Item>
                          <Dropdown.Item
                            href="#"
                            className="d-flex align-items-center"
                            onClick={() => onDelete(selectedThread.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} className="me-2" />{" "}
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>

                  <hr className="p-0 mt-0" />

                  {/* Email Messages Section */}
                  <div
                    className="mt-2"
                    style={{ height: "78vh", overflow: "scroll" }}
                  >
                    {selectedThread.messages.map((msg) => {
                      const isCollapsed = collapsedIds[msg.id] || false;

                      const toggleCollapse = (id) => {
                        setCollapsedIds((prev) => ({
                          ...prev,
                          [id]: !prev[id],
                        }));
                      };

                      return (
                        <div
                          key={msg.id}
                          className="border rounded p-2 mb-3 bg-light"
                        >
                          <div
                            className="text-muted small d-flex justify-content-between"
                            onClick={() => toggleCollapse(msg.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="d-flex justify-content-between gap-1">
                              <img
                                src={imgs}
                                alt=""
                                style={{
                                  height: "30px",
                                  width: "30px",
                                  borderRadius: "50%",
                                }}
                              />
                              <div className="d-flex flex-column">
                                {msg.from} <span>{msg.subject}</span>
                              </div>
                            </div>

                            <div className="d-flex flex-column">
                              {formatDate(new Date(msg.date) || "")}
                              <span>
                                {formatTime(
                                  new Date(msg.date).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }),
                                )}
                              </span>
                            </div>
                          </div>

                          <hr />

                          {isCollapsed ? (
                            <div
                              className="email-content"
                              style={{
                                whiteSpace: "normal",
                                fontSize: "12px",
                                fontWeight: "300",
                              }}
                              dangerouslySetInnerHTML={{
                                __html:
                                  msg.body && msg.body.length > 100
                                    ? `${msg.body.slice(0, 100)}...`
                                    : msg.body,
                              }}
                            />
                          ) : (
                            ""
                          )}

                          <div
                            className={`collapse-section ${
                              isCollapsed ? "collapsed" : "expanded"
                            }`}
                            style={{
                              overflow: "hidden",
                              transition: "max-height 0.3s ease",
                              maxHeight: isCollapsed ? "0px" : "2000px",
                            }}
                          >
                            <div
                              className="email-content"
                              style={{
                                whiteSpace: "normal",
                                fontSize: "12px",
                                fontWeight: "300",
                              }}
                              dangerouslySetInnerHTML={{ __html: msg.body }}
                            />

                            {Array.isArray(msg.attachments) &&
                              msg.attachments.map((att, idx) => {
                                if (att.mime?.startsWith("image/")) {
                                  return (
                                    <div key={idx} className="text-center mt-2">
                                      <img
                                        src={att.dataUrl}
                                        alt={att.filename}
                                        style={{
                                          maxWidth: "100%",
                                          borderRadius: 8,
                                          height: "300px",
                                          cursor: "pointer",
                                          transition: "transform 0.2s ease",
                                        }}
                                        onClick={() =>
                                          setPreviewImage(att.dataUrl)
                                        }
                                        onMouseOver={(e) =>
                                          (e.currentTarget.style.transform =
                                            "scale(1.02)")
                                        }
                                        onMouseOut={(e) =>
                                          (e.currentTarget.style.transform =
                                            "scale(1)")
                                        }
                                      />
                                      <a
                                        href={att.dataUrl}
                                        download={att.filename}
                                        className="btn btn-sm btn-outline-secondary mt-2"
                                      >
                                        📎 Download {att.filename}
                                      </a>
                                    </div>
                                  );
                                } else if (att.mime === "application/pdf") {
                                  return (
                                    <div key={idx} className="text-center">
                                      <iframe
                                        src={att.dataUrl}
                                        title={att.filename}
                                        style={{
                                          width: "100%",
                                          height: "400px",
                                          borderRadius: 8,
                                          border: "1px solid #ccc",
                                        }}
                                      />
                                      <a
                                        href={att.dataUrl}
                                        download={att.filename}
                                        className="btn btn-sm btn-outline-secondary mt-2"
                                      >
                                        📄 Download {att.filename}
                                      </a>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div key={idx} className="text-center">
                                      <a
                                        href={att.dataUrl}
                                        download={att.filename}
                                        className="btn btn-sm btn-outline-secondary mt-2"
                                      >
                                        📎 Download {att.filename}
                                      </a>
                                    </div>
                                  );
                                }
                              })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Image Preview Overlay */}
            {previewImage && (
              <div
                className="image-preview-overlay"
                onClick={() => setPreviewImage(null)}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0,0,0,0.85)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                }}
              >
                <div className="image" onClick={(e) => e.stopPropagation()}>
                  <style>
                    {`.image{
              max-height:100% !important;
              max-width:70% !important;
              overflow: auto !important;
              padding : 20px !important
            }
            `}
                  </style>
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      borderRadius: 8,
                    }}
                  />
                  <div
                    className="preview-actions"
                    style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      display: "flex",
                      gap: "0.5rem",
                      opacity: 0.9,
                    }}
                  >
                    <a
                      href={previewImage}
                      download
                      className="btn btn-sm btn-light rounded-circle shadow"
                      title="Download"
                    >
                      ⬇️
                    </a>
                    <button
                      className="btn btn-sm btn-light rounded-circle shadow"
                      title="Close"
                      onClick={() => setPreviewImage(null)}
                    >
                      ✖
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {composeOpen && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ zIndex: 1055 }}
          >
            <div
              className="w-100 h-100 bg-dark bg-opacity-50"
              onClick={handleComposedClose}
            />
            <div
              className="position-absolute top-0 end-0 h-100 bg-white border-start d-flex flex-column"
              style={{ width: "800px", maxWidth: "100vw" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                className="d-flex align-items-center justify-content-between p-3 border-bottom bg-white"
                style={{ flexShrink: 0 }}
              >
                <div className="fw-semibold fs-5">
                  {composeMode === "reply"
                    ? "Reply"
                    : composeMode === "replyAll"
                      ? "Reply All"
                      : composeMode === "forward"
                        ? "Forward"
                        : "New Email"}
                </div>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setComposeOpen(false)}
                >
                  Close
                </button>
              </div>

              {/* Content */}
              <div className="flex-grow-1 overflow-auto p-3">
                {error ? (
                  <div className="mb-3 small text-danger">{error}</div>
                ) : null}

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">To</label>
                    <input
                      className="form-control"
                      value={composeData.to}
                      onChange={(e) =>
                        setComposeData((d) => ({ ...d, to: e.target.value }))
                      }
                      placeholder="recipient@example.com, second@example.com"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Cc</label>
                    <input
                      className="form-control"
                      value={composeData.cc}
                      onChange={(e) =>
                        setComposeData((d) => ({ ...d, cc: e.target.value }))
                      }
                      placeholder="optional, comma separated"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Bcc</label>
                    <input
                      className="form-control"
                      value={composeData.bcc}
                      onChange={(e) =>
                        setComposeData((d) => ({ ...d, bcc: e.target.value }))
                      }
                      placeholder="optional, comma separated"
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Subject</label>
                    <input
                      className="form-control"
                      value={composeData.subject}
                      onChange={(e) =>
                        setComposeData((d) => ({
                          ...d,
                          subject: e.target.value,
                        }))
                      }
                      placeholder="Subject"
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Message</label>
                    <ReactQuill
                      theme="snow"
                      style={{ minHeight: "240px" }}
                      value={composeData.body}
                      onChange={(val) =>
                        setComposeData((d) => ({ ...d, body: val }))
                      }
                      placeholder="Write your message..."
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Attachments</label>
                    <input
                      type="file"
                      multiple
                      className="form-control"
                      onChange={(e) => onSelectFiles(e)}
                    />
                    {composeData.attachments?.length ? (
                      <div className="mt-2 small text-muted">
                        {composeData.attachments.length} file(s) attached
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className="border-top p-3 bg-white"
                style={{ flexShrink: 0 }}
              >
                <div className="d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setComposeOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={sending}
                    onClick={onSend}
                  >
                    {sending ? "Sending..." : "Send"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
